/* eslint-disable react-refresh/only-export-components */
import { cn } from '@/lib/utils';
import type {
  Cell,
  Column,
  ColumnDef,
  Header,
  HeaderGroup,
  Row,
  SortingState,
  Table,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { HTMLAttributes, ReactNode } from 'react';
import React, { createContext, useContext, useState, useEffect, useRef, ReactElement } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useGridColumns } from '@/components/crm/DataGrid';

type TableState = {
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
};

export const useTableStore = create<TableState>()(
  devtools((set) => ({
    sorting: [],
    setSorting: (sorting: SortingState) => set(() => ({ sorting })),
  }))
);

export const TableContext = createContext<{
  data: unknown[];
  columns: ColumnDef<unknown, unknown>[];
  table: Table<unknown> | null;
}>({
  data: [],
  columns: [],
  table: null,
});

export const Table = ({ className, ...props }: HTMLAttributes<HTMLTableElement>) => (
  <table className={cn('min-w-full', className)} {...props} />
);
Table.displayName = "Table";

export const TableHeader = ({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) => (
  <thead className={cn('bg-gray-50 border-b border-gray-200', className)} {...props} />
);
TableHeader.displayName = "TableHeader";

export const TableBody = ({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody className={cn('bg-white divide-y divide-gray-100', className)} {...props} />
);
TableBody.displayName = "TableBody";

export const TableRow = ({ className, onClick, ...props }: HTMLAttributes<HTMLTableRowElement>) => (
  <tr 
    className={cn('transition-colors border-b border-gray-100 hover:bg-gray-50/70', onClick && 'cursor-pointer', className)} 
    onClick={onClick}
    {...props} 
  />
);
TableRow.displayName = "TableRow";

export const TableCell = ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
  <td className={cn('px-4 py-3 text-xs text-gray-700 whitespace-nowrap border-r border-gray-100 last:border-r-0', className)} {...props} />
);
TableCell.displayName = "TableCell";

export const TableHead = ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
  <th className={cn('px-4 py-3 text-left text-xs font-medium text-gray-500 whitespace-nowrap border-r border-gray-200/70 last:border-r-0', className)} {...props} />
);
TableHead.displayName = "TableHead";

export const TableActions = ({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-3 sm:px-6 py-2 text-right text-xs whitespace-nowrap', className)} {...props}>
    <div className="flex justify-end">
      {children}
    </div>
  </div>
);
TableActions.displayName = "TableActions";

export const TableActionsHead = ({ className, ...props }: HTMLAttributes<HTMLTableCellElement>) => (
  <th className={cn('px-3 sm:px-6 py-2 text-right text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap', className)} {...props} />
);
TableActionsHead.displayName = "TableActionsHead";

export const TableDropdown = ({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('relative inline-block', className)} {...props}>
    {children}
  </div>
);
TableDropdown.displayName = "TableDropdown";

export const TableDropdownButton = ({ className, ...props }: HTMLAttributes<HTMLButtonElement> & { type?: 'button' | 'submit' | 'reset' }) => (
  <button
    type="button"
    className={cn('inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-black hover:bg-gray-100 transition-colors', className)}
    {...props}
  >
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
      <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"/>
    </svg>
  </button>
);
TableDropdownButton.displayName = "TableDropdownButton";

interface TableDropdownPopupProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; left: number };
}

const TableDropdownPopup = ({ 
  className, 
  isOpen, 
  onClose, 
  position,
  children, 
  ...props 
}: TableDropdownPopupProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const rect = dropdown.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      if (rect.bottom > viewportHeight) {
        dropdown.style.top = `${position.top - rect.height - 40}px`;
      }

      if (rect.right > viewportWidth) {
        dropdown.style.left = `${viewportWidth - rect.width - 16}px`;
      }
    }
  }, [isOpen, position]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className={cn('fixed z-[100] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px] animate-scale-in', className)}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      {...props}
    >
      {children}
    </div>
  );
};
TableDropdownPopup.displayName = "TableDropdownPopup";

export const TableDropdownMenu = ({ 
  className, 
  isOpen, 
  onClose, 
  position = { top: 0, left: 0 },
  children, 
  ...props 
}: TableDropdownPopupProps) => (
  <TableDropdownPopup
    isOpen={isOpen}
    onClose={onClose}
    position={position}
    className={className}
    {...props}
  >
    {children}
  </TableDropdownPopup>
);
TableDropdownMenu.displayName = "TableDropdownMenu";

export const TableDropdownItem = ({ className, children, ...props }: HTMLAttributes<HTMLButtonElement> & { type?: 'button' | 'submit' | 'reset' }) => (
  <button
    type="button"
    className={cn('flex items-center gap-3 w-full text-left px-4 py-3 text-xs text-gray-700 hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg border-b border-gray-100 last:border-b-0', className)}
    {...props}
  >
    {children}
  </button>
);
TableDropdownItem.displayName = "TableDropdownItem";

export const useTableDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    
    const viewportHeight = window.innerHeight;
    const shouldShowAbove = rect.bottom > viewportHeight / 1.5;
    
    const left = rect.right - 160;
    const top = shouldShowAbove ? rect.top - 10 : rect.bottom + 4;

    setPosition({ top, left });
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    position,
    handleOpen,
    handleClose,
  };
};

export type TableProviderProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  children: ReactNode;
  className?: string;
  /** Show the "Add row" button under the table. Omit to hide it. */
  onAddRow?: () => void;
  addRowLabel?: string;
  /** Row checkboxes and the floating selection bar. On by default. */
  selectable?: boolean;
  /** Optional bulk action shown in the selection bar. */
  onDeleteSelected?: (rows: TData[]) => void;
};

/** A column's id as TanStack resolves it (explicit id, else accessorKey). */
const columnId = (c: ColumnDef<never, never> | ColumnDef<unknown, unknown> | { id?: string; accessorKey?: unknown }): string =>
  (c as { id?: string }).id ?? String((c as { accessorKey?: unknown }).accessorKey ?? '');
const columnLabel = (c: unknown): string => {
  const header = (c as { header?: unknown }).header;
  if (typeof header === 'string' && header.trim()) return header;
  const id = columnId(c as { id?: string });
  return id === 'actions' ? 'Actions' : id.replace(/_/g, ' ').replace(/^\w/, (x) => x.toUpperCase());
};

const exportRowsToCsv = (rows: Record<string, unknown>[]) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]).filter((k) => rows.some((r) => ['string', 'number', 'boolean'].includes(typeof r[k])));
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [keys.join(','), ...rows.map((r) => keys.map((k) => esc(r[k])).join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const SelectBox = ({ checked, indeterminate, onChange, label }: {
  checked: boolean; indeterminate?: boolean; onChange: (v: boolean) => void; label: string;
}) => (
  <input
    type="checkbox"
    aria-label={label}
    checked={checked}
    ref={(el) => { if (el) el.indeterminate = !!indeterminate; }}
    onChange={(e) => onChange(e.target.checked)}
    onClick={(e) => e.stopPropagation()}
    className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
  />
);

export function TableProvider<TData, TValue>({
  columns,
  data,
  children,
  className,
  onAddRow,
  addRowLabel = 'Add row',
  selectable = true,
  onDeleteSelected,
}: TableProviderProps<TData, TValue>) {
  const { sorting, setSorting } = useTableStore();
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const grid = useGridColumns();

  // Tell the page's Customize menu which columns exist (labels from string headers).
  const register = grid?.register;
  const columnKey = columns.map((c) => columnId(c)).join('|');
  React.useEffect(() => {
    register?.(columns.map((c) => ({ id: columnId(c), label: columnLabel(c) })));
  }, [register, columnKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const columnVisibility = React.useMemo(
    () => Object.fromEntries(Object.entries(grid?.hidden ?? {}).filter(([, h]) => h).map(([id]) => [id, false])),
    [grid?.hidden]
  );

  const allColumns = React.useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!selectable) return columns;
    const selectColumn: ColumnDef<TData, TValue> = {
      id: '__select',
      enableSorting: false,
      header: ({ table }) => (
        <SelectBox
          label="Select all rows"
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={(v) => table.toggleAllRowsSelected(v)}
        />
      ),
      cell: ({ row }) => (
        <SelectBox label="Select row" checked={row.getIsSelected()} onChange={(v) => row.toggleSelected(v)} />
      ),
    };
    return [selectColumn, ...columns];
  }, [columns, selectable]);

  const table = useReactTable({
    data,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: selectable,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
    },
  });

  const selectedRows = table.getSelectedRowModel().rows;

  return (
    <TableContext.Provider
      value={{
        data,
        columns: allColumns as never,
        table: table as never,
      }}
    >
      <div className="overflow-x-auto grid-scroll">
        <Table className={className}>{children}</Table>
      </div>

      {onAddRow && (
        <button
          type="button"
          onClick={onAddRow}
          className="flex w-full items-center gap-2 px-6 py-2.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-primary/10 border-t border-gray-200 transition-colors"
        >
          <span className="text-base leading-none">+</span> {addRowLabel}
        </button>
      )}

      {selectable && selectedRows.length > 0 && (
        <div className="sticky bottom-4 z-20 flex justify-center px-3 pointer-events-none">
          <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs shadow-xl">
            <span className="px-1 text-gray-500"><span className="font-semibold text-gray-900">{selectedRows.length}</span> Selected</span>
            {!table.getIsAllRowsSelected() && (
              <button type="button" onClick={() => table.toggleAllRowsSelected(true)} className="rounded-lg px-2 py-1.5 text-primary hover:bg-primary/5">
                Select all {table.getRowModel().rows.length}
              </button>
            )}
            <button
              type="button"
              onClick={() => exportRowsToCsv(selectedRows.map((r) => r.original as Record<string, unknown>))}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" /></svg>
              Export selected
            </button>
            {onDeleteSelected && (
              <button
                type="button"
                onClick={() => { onDeleteSelected(selectedRows.map((r) => r.original as TData)); setRowSelection({}); }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 font-medium text-red-600 hover:bg-red-50"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M10 11v6m4-6v6M5 7l1 12a2 2 0 002 2h8a2 2 0 002-2l1-12M9 7V4h6v3" /></svg>
                Delete
              </button>
            )}
            <button type="button" onClick={() => setRowSelection({})} className="rounded-lg px-2 py-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-900" aria-label="Clear selection">✕</button>
          </div>
        </div>
      )}
    </TableContext.Provider>
  );
}

export type DataTableHeadProps = {
  header: Header<unknown, unknown>;
  className?: string;
};

export const DataTableHead = ({ header, className }: DataTableHeadProps) => (
  <TableHead key={header.id} className={className}>
    {header.isPlaceholder
      ? null
      : flexRender(header.column.columnDef.header, header.getContext())}
  </TableHead>
);

export type DataTableHeaderGroupProps = {
  headerGroup: HeaderGroup<unknown>;
  children: (props: { header: Header<unknown, unknown> }) => ReactNode;
};

export const DataTableHeaderGroup = ({
  headerGroup,
}: DataTableHeaderGroupProps) => (
  <TableRow key={headerGroup.id}>
    {headerGroup.headers.map((header) => (
      <DataTableHead key={header.id} header={header} />
    ))}
  </TableRow>
);

export type DataTableHeaderProps = {
  className?: string;
  children: (props: { headerGroup: HeaderGroup<unknown> }) => ReactNode;
};

export const DataTableHeader = ({ className, children }: DataTableHeaderProps) => {
  const { table } = useContext(TableContext);

  return (
    <TableHeader className={className}>
      {table?.getHeaderGroups().map((headerGroup) =>
        React.cloneElement(children({ headerGroup }) as ReactElement, { key: headerGroup.id })
      )}
    </TableHeader>
  );
};

export interface TableColumnHeaderProps<TData, TValue>
  extends HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
}

export function TableColumnHeader<TData, TValue>({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  column,
  title,
  className,
}: TableColumnHeaderProps<TData, TValue>) {
  return <div className={cn('text-xs whitespace-nowrap', className)}>{title}</div>;
}

export type DataTableCellProps = {
  cell: Cell<unknown, unknown>;
  className?: string;
};

export const DataTableCell = ({ cell, className }: DataTableCellProps) => (
  <TableCell className={className}>
    {flexRender(cell.column.columnDef.cell, cell.getContext())}
  </TableCell>
);

export type DataTableRowProps = {
  row: Row<unknown>;
  children: (props: { cell: Cell<unknown, unknown> }) => ReactNode;
  className?: string;
  onClick?: () => void;
};

export const DataTableRow = ({ row, children, className, onClick }: DataTableRowProps) => (
  <TableRow
    key={row.id}
    data-state={row.getIsSelected() && 'selected'}
    className={cn(
      row.getIsSelected() && 'bg-primary/10 shadow-[inset_3px_0_0_0_var(--color-primary,#ff6d27)]',
      className
    )}
    onClick={onClick}
  >
    {row.getVisibleCells().map((cell) =>
      React.cloneElement(children({ cell }) as ReactElement, { key: cell.id })
    )}
  </TableRow>
);

export type DataTableBodyProps = {
  children: (props: { row: Row<unknown> }) => ReactNode;
  className?: string;
};

export const DataTableBody = ({ children, className }: DataTableBodyProps) => {
  const { table } = useContext(TableContext);
  const rows = table?.getRowModel().rows;

  return (
    <TableBody className={className}>
      {rows?.map((row) => React.cloneElement(children({ row }) as ReactElement, { key: row.id }))}
    </TableBody>
  );
};